import {
  ResizablePanel,
} from "@/components/ui/resizable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileAttachment } from "@/types/attachment";
import { AttachmentsPanel } from "./AttachmentsPanel";
import { CommentCard } from "./CommentCard";

interface TextSelection {
  start: number;
  end: number;
  text: string;
  originalText: string;
}

interface Comment {
  id: string;
  text: string;
  color: string;
  number: number;
  textSelections: TextSelection[];
  isGeneral: boolean;
}

interface AggregatedAnnotationsPanelProps {
  // General analysis-level comments (editable)
  generalComments: Comment[];
  onAddGeneralComment: (text: string) => void;
  onDeleteGeneralComment: (commentId: string) => void;
  onEditGeneralComment: (commentId: string, newText: string) => void;
  
  // Aggregated tab comments (read-only)
  currentTabComments: Comment[];
  synthesisTabComments: Comment[];
  originalTabComments: Comment[];
  onNavigateToComment: (commentId: string, sourceTab: 'current' | 'synthesis' | 'original') => void;

  // Attachments
  attachments: FileAttachment[];
  onAddAttachment: (files: File[]) => void;
  onDeleteAttachment: (attachmentId: string) => void;
  onHighlightAttachment: (attachmentId: string | null) => void;
  highlightedAttachmentId: string | null;
}

export const AggregatedAnnotationsPanel = ({
  generalComments,
  onAddGeneralComment,
  onDeleteGeneralComment,
  onEditGeneralComment,
  currentTabComments,
  synthesisTabComments,
  originalTabComments,
  onNavigateToComment,
  attachments,
  onAddAttachment,
  onDeleteAttachment,
  onHighlightAttachment,
  highlightedAttachmentId,
}: AggregatedAnnotationsPanelProps) => {
  const renderTabComments = (
    comments: Comment[], 
    sourceTab: 'current' | 'synthesis' | 'original',
    tabLabel: string
  ) => {
    if (comments.length === 0) {
      return (
        <div className="text-sm text-muted-foreground italic p-2">
          No annotations in {tabLabel}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {comments.map((comment) => (
          <CommentCard
            key={comment.id}
            comment={comment}
            readOnly={true}
            onClick={() => onNavigateToComment(comment.id, sourceTab)}
            showExternalLink={true}
          />
        ))}
      </div>
    );
  };

  return (
    <ResizablePanel defaultSize={30} minSize={20} maxSize={50}>
      <Tabs defaultValue="overview" className="h-full flex flex-col">
        <TabsList className="w-full rounded-none border-b">
          <TabsTrigger value="overview" className="flex-1">
            Overview
          </TabsTrigger>
          <TabsTrigger value="attachments" className="flex-1">
            Attachments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="flex-1 min-h-0 m-0 h-0" forceMount>
          <ScrollArea className="h-full">
            <div className="p-4 space-y-6">
              {/* General Analysis Comments */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-primary"></span>
                  General Notes
                </h3>
                <div className="space-y-2">
                  {generalComments.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      comment={comment}
                      readOnly={true}
                      showExternalLink={false}
                    />
                  ))}
                  {generalComments.length === 0 && (
                    <div className="text-sm text-muted-foreground italic">
                      No general notes yet
                    </div>
                  )}
                </div>
              </div>

              {/* Current Tab Annotations */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  Current Tab ({currentTabComments.length})
                </h3>
                {renderTabComments(currentTabComments, 'current', 'Current')}
              </div>

              {/* Synthesis Tab Annotations */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  Synthesis Tab ({synthesisTabComments.length})
                </h3>
                {renderTabComments(synthesisTabComments, 'synthesis', 'Synthesis')}
              </div>

              {/* Original Tab Annotations */}
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  Original Tab ({originalTabComments.length})
                </h3>
                {renderTabComments(originalTabComments, 'original', 'Original')}
              </div>
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="attachments" className="flex-1 min-h-0 m-0 h-0" forceMount>
          <AttachmentsPanel
            attachments={attachments}
            onDeleteAttachment={onDeleteAttachment}
            onAddAttachment={onAddAttachment}
            onHighlightAttachment={onHighlightAttachment}
            highlightedAttachmentId={highlightedAttachmentId}
          />
        </TabsContent>
      </Tabs>
    </ResizablePanel>
  );
};
